import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserSecret, faPlus, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { COMPLIANCE_NAV } from './nav';

interface DSR {
    id: number; requester_name: string; requester_email: string; request_type: string; request_type_label: string;
    status: string; details: string; response_notes: string; patient: string | null; handled_by: string | null;
    received_at: string; due_at: string | null; completed_at: string | null; is_overdue: boolean;
}

const STATUS_COLORS: Record<string, string> = {
    received: 'warning', verifying: 'info', in_progress: 'primary', fulfilled: 'success', refused: 'secondary',
};
const STATUSES = ['received', 'verifying', 'in_progress', 'fulfilled', 'refused'];
const TYPES = [
    { v: 'access', l: 'Access (Art. 15)' }, { v: 'export', l: 'Portability / Export (Art. 20)' },
    { v: 'erasure', l: 'Erasure (Art. 17)' }, { v: 'rectification', l: 'Rectification (Art. 16)' },
    { v: 'restriction', l: 'Restriction (Art. 18)' },
];

const DataSubjectRequests: React.FC = () => {
    const { api } = useInternalApi();
    const [requests, setRequests] = useState<DSR[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ requester_name: '', requester_email: '', request_type: 'access', details: '' });
    const [viewing, setViewing] = useState<DSR | null>(null);
    const [notes, setNotes] = useState('');

    useEffect(() => { document.title = 'Data Subject Requests - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const res = await api.get(`compliance-api/data-subject-requests/?${params}`);
            if (res.data?.status === 'success') setRequests(res.data.data || []);
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const submitCreate = async () => {
        if (!form.requester_name.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Requester name is required.' });
            return;
        }
        try {
            await api.post('compliance-api/data-subject-requests/', form);
            setShowCreate(false);
            setForm({ requester_name: '', requester_email: '', request_type: 'access', details: '' });
            load();
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not log request.' });
        }
    };

    const patchDsr = async (d: DSR, payload: any) => {
        try {
            const res = await api.patch(`compliance-api/data-subject-requests/${d.id}/`, payload);
            if (res.data?.status === 'success') {
                if (viewing?.id === d.id) setViewing(res.data.data);
                load();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update request.' });
        }
    };

    const overdue = requests.filter(r => r.is_overdue).length;

    return (
        <InternalShell activeDashboard="compliance_center" subNav={COMPLIANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUserSecret} className="me-2 text-primary" />Data Subject Requests</h2>
                    <p className="text-muted mb-0">Statutory access, portability and erasure requests — 30-day response clock</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Log Request</button>
            </div>

            {overdue > 0 && (
                <div className="alert alert-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>{overdue} request(s) past the statutory deadline.</strong>
                </div>
            )}

            <div className="alert alert-secondary small">
                Fulfilment is recorded here, not executed here. Erasure in particular touches clinical records under separate retention obligations, so it is performed deliberately by a person — this workflow tracks and evidences the response.
            </div>

            <select className="form-select mb-3" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : requests.length === 0 ? (
                        <div className="text-center py-5 text-muted">No data subject requests on record.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Requester</th><th>Type</th><th>Status</th><th>Received</th><th>Due</th><th></th></tr></thead>
                            <tbody>
                                {requests.map(d => (
                                    <tr key={d.id} className={d.is_overdue ? 'table-danger' : ''} style={{ cursor: 'pointer' }} onClick={() => { setViewing(d); setNotes(d.response_notes); }}>
                                        <td>{d.requester_name}<div className="small text-muted">{d.requester_email}</div></td>
                                        <td className="small">{d.request_type_label}</td>
                                        <td><span className={`badge bg-${STATUS_COLORS[d.status]}`}>{d.status.replace('_', ' ')}</span></td>
                                        <td className="small">{new Date(d.received_at).toLocaleDateString()}</td>
                                        <td className="small">{d.due_at ? new Date(d.due_at).toLocaleDateString() : '—'}</td>
                                        <td>{d.is_overdue && <span className="badge bg-danger">Overdue</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Log Data Subject Request</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Requester name</label>
                                    <input className="form-control" value={form.requester_name} onChange={(e) => setForm(f => ({ ...f, requester_name: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Email</label>
                                    <input type="email" className="form-control" value={form.requester_email} onChange={(e) => setForm(f => ({ ...f, requester_email: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Request type</label>
                                    <select className="form-select" value={form.request_type} onChange={(e) => setForm(f => ({ ...f, request_type: e.target.value }))}>
                                        {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Details</label>
                                    <textarea className="form-control" rows={3} value={form.details} onChange={(e) => setForm(f => ({ ...f, details: e.target.value }))} />
                                </div>
                                <div className="form-text">A 30-day statutory response deadline starts on save.</div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Log Request</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.request_type_label} — {viewing.requester_name}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                {viewing.is_overdue && <div className="alert alert-danger py-2">Past the statutory deadline of {viewing.due_at ? new Date(viewing.due_at).toLocaleDateString() : '—'}.</div>}
                                <p>{viewing.details || <span className="text-muted">No details recorded.</span>}</p>
                                <div className="mb-3">
                                    <label className="form-label small">Status</label>
                                    <select className="form-select form-select-sm" value={viewing.status} onChange={(e) => patchDsr(viewing, { status: e.target.value })}>
                                        {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                                    </select>
                                </div>
                                <label className="form-label small">Response notes — evidence of what was provided or why refused</label>
                                <textarea className="form-control" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                                {viewing.completed_at && <div className="form-text">Completed {new Date(viewing.completed_at).toLocaleString()} by {viewing.handled_by}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                <button className="btn btn-primary" onClick={() => patchDsr(viewing, { response_notes: notes })}>Save Notes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default DataSubjectRequests;
